/**
 * ECS事件类型枚举
 * 定义实体组件系统中的所有事件类型
 */
export var ECSEventType;
(function (ECSEventType) {
    // 实体相关事件
    ECSEventType["ENTITY_CREATED"] = "entity:created";
    ECSEventType["ENTITY_DESTROYED"] = "entity:destroyed";
    ECSEventType["ENTITY_ENABLED"] = "entity:enabled";
    ECSEventType["ENTITY_DISABLED"] = "entity:disabled";
    ECSEventType["ENTITY_TAG_ADDED"] = "entity:tag:added";
    ECSEventType["ENTITY_TAG_REMOVED"] = "entity:tag:removed";
    ECSEventType["ENTITY_NAME_CHANGED"] = "entity:name:changed";
    // 组件相关事件
    ECSEventType["COMPONENT_ADDED"] = "component:added";
    ECSEventType["COMPONENT_REMOVED"] = "component:removed";
    ECSEventType["COMPONENT_MODIFIED"] = "component:modified";
    ECSEventType["COMPONENT_ENABLED"] = "component:enabled";
    ECSEventType["COMPONENT_DISABLED"] = "component:disabled";
    // 系统相关事件
    ECSEventType["SYSTEM_ADDED"] = "system:added";
    ECSEventType["SYSTEM_REMOVED"] = "system:removed";
    ECSEventType["SYSTEM_ENABLED"] = "system:enabled";
    ECSEventType["SYSTEM_DISABLED"] = "system:disabled";
    ECSEventType["SYSTEM_PROCESSING_START"] = "system:processing:start";
    ECSEventType["SYSTEM_PROCESSING_END"] = "system:processing:end";
    ECSEventType["SYSTEM_ERROR"] = "system:error";
    // 场景相关事件
    ECSEventType["SCENE_CREATED"] = "scene:created";
    ECSEventType["SCENE_DESTROYED"] = "scene:destroyed";
    ECSEventType["SCENE_ACTIVATED"] = "scene:activated";
    ECSEventType["SCENE_DEACTIVATED"] = "scene:deactivated";
    ECSEventType["SCENE_PAUSED"] = "scene:paused";
    ECSEventType["SCENE_RESUMED"] = "scene:resumed";
    // 查询相关事件
    ECSEventType["QUERY_EXECUTED"] = "query:executed";
    ECSEventType["QUERY_CACHE_HIT"] = "query:cache:hit";
    ECSEventType["QUERY_CACHE_MISS"] = "query:cache:miss";
    ECSEventType["QUERY_OPTIMIZED"] = "query:optimized";
    // 性能相关事件
    ECSEventType["PERFORMANCE_WARNING"] = "performance:warning";
    ECSEventType["PERFORMANCE_CRITICAL"] = "performance:critical";
    ECSEventType["MEMORY_USAGE_HIGH"] = "memory:usage:high";
    ECSEventType["FRAME_RATE_DROP"] = "frame:rate:drop";
    // 索引相关事件
    ECSEventType["INDEX_CREATED"] = "index:created";
    ECSEventType["INDEX_UPDATED"] = "index:updated";
    ECSEventType["INDEX_OPTIMIZED"] = "index:optimized";
    // Archetype相关事件
    ECSEventType["ARCHETYPE_CREATED"] = "archetype:created";
    ECSEventType["ARCHETYPE_ENTITY_ADDED"] = "archetype:entity:added";
    ECSEventType["ARCHETYPE_ENTITY_REMOVED"] = "archetype:entity:removed";
    // 脏标记相关事件
    ECSEventType["DIRTY_MARK_ADDED"] = "dirty:mark:added";
    ECSEventType["DIRTY_BATCH_PROCESSED"] = "dirty:batch:processed";
    // 错误和警告事件
    ECSEventType["ERROR_OCCURRED"] = "error:occurred";
    ECSEventType["WARNING_ISSUED"] = "warning:issued";
    // 生命周期事件
    ECSEventType["FRAMEWORK_INITIALIZED"] = "framework:initialized";
    ECSEventType["FRAMEWORK_SHUTDOWN"] = "framework:shutdown";
    // 调试相关事件
    ECSEventType["DEBUG_INFO"] = "debug:info";
    ECSEventType["DEBUG_STATS_UPDATED"] = "debug:stats:updated";
})(ECSEventType || (ECSEventType = {}));
/**
 * 事件优先级枚举
 * 定义事件处理的优先级级别
 */
export var EventPriority;
(function (EventPriority) {
    EventPriority[EventPriority["LOWEST"] = 0] = "LOWEST";
    EventPriority[EventPriority["LOW"] = 25] = "LOW";
    EventPriority[EventPriority["NORMAL"] = 50] = "NORMAL";
    EventPriority[EventPriority["HIGH"] = 75] = "HIGH";
    EventPriority[EventPriority["HIGHEST"] = 100] = "HIGHEST";
    EventPriority[EventPriority["CRITICAL"] = 200] = "CRITICAL";
})(EventPriority || (EventPriority = {}));
/**
 * 预定义的事件类型常量
 * 提供类型安全的事件类型字符串
 */
export const EVENT_TYPES = {
    // 实体事件
    ENTITY: {
        CREATED: ECSEventType.ENTITY_CREATED,
        DESTROYED: ECSEventType.ENTITY_DESTROYED,
        ENABLED: ECSEventType.ENTITY_ENABLED,
        DISABLED: ECSEventType.ENTITY_DISABLED,
        TAG_ADDED: ECSEventType.ENTITY_TAG_ADDED,
        TAG_REMOVED: ECSEventType.ENTITY_TAG_REMOVED,
        NAME_CHANGED: ECSEventType.ENTITY_NAME_CHANGED
    },
    // 组件事件
    COMPONENT: {
        ADDED: ECSEventType.COMPONENT_ADDED,
        REMOVED: ECSEventType.COMPONENT_REMOVED,
        MODIFIED: ECSEventType.COMPONENT_MODIFIED,
        ENABLED: ECSEventType.COMPONENT_ENABLED,
        DISABLED: ECSEventType.COMPONENT_DISABLED
    },
    // 系统事件
    SYSTEM: {
        ADDED: ECSEventType.SYSTEM_ADDED,
        REMOVED: ECSEventType.SYSTEM_REMOVED,
        ENABLED: ECSEventType.SYSTEM_ENABLED,
        DISABLED: ECSEventType.SYSTEM_DISABLED,
        PROCESSING_START: ECSEventType.SYSTEM_PROCESSING_START,
        PROCESSING_END: ECSEventType.SYSTEM_PROCESSING_END,
        ERROR: ECSEventType.SYSTEM_ERROR
    },
    // 性能事件
    PERFORMANCE: {
        WARNING: ECSEventType.PERFORMANCE_WARNING,
        CRITICAL: ECSEventType.PERFORMANCE_CRITICAL,
        MEMORY_HIGH: ECSEventType.MEMORY_USAGE_HIGH,
        FRAME_DROP: ECSEventType.FRAME_RATE_DROP
    }
};
/**
 * 事件类型验证器
 * 验证事件类型是否有效
 */
export class EventTypeValidator {
    /**
     * 验证事件类型是否有效
     * @param eventType 事件类型
     * @returns 是否有效
     */
    static isValid(eventType) {
        return this.validTypes.has(eventType);
    }
    /**
     * 获取所有有效的事件类型
     * @returns 事件类型数组
     */
    static getAllValidTypes() {
        return Array.from(this.validTypes);
    }
    /**
     * 添加自定义事件类型
     * @param eventType 事件类型
     */
    static addCustomType(eventType) {
        this.validTypes.add(eventType);
    }
    /**
     * 移除自定义事件类型
     * @param eventType 事件类型
     */
    static removeCustomType(eventType) {
        this.validTypes.delete(eventType);
    }
}
EventTypeValidator.validTypes = new Set([
    ...Object.values(ECSEventType),
    ...Object.values(EVENT_TYPES.ENTITY),
    ...Object.values(EVENT_TYPES.COMPONENT),
    ...Object.values(EVENT_TYPES.SYSTEM),
    ...Object.values(EVENT_TYPES.PERFORMANCE)
]);
//# sourceMappingURL=CoreEvents.js.map